import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileBarChart, Eye, Download, Loader2, Sheet, FileText, Building2, DoorClosed, ChevronRight, Trash2, ShieldCheck, Share2, Undo2 } from "lucide-react";
import { savedReportsApi, exportsApi, buildingsApi, doorsApi } from "@/services/api";
import { downloadBinaryResponse } from "@/lib/download";
import { useAsync } from "@/hooks/useAsync";
import { fmtDateTime, apiError, prettyStatus, statusTone } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { usePlan } from "@/context/PlanContext";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

function base64ToBlobUrl(b64) {
  const clean = String(b64).replace(/^data:application\/pdf;base64,/, "");
  const chars = atob(clean);
  const bytes = new Uint8Array(chars.length);
  for (let i = 0; i < chars.length; i++) bytes[i] = chars.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
}

const EXPORTS = [
  { key: "inspections", label: "Inspections", fn: (a) => a.inspectionsXlsx() },
  { key: "doors", label: "Doors", fn: (a) => a.doorsXlsx() },
  { key: "quotes", label: "Quotes", fn: (a) => a.quotesXlsx() },
  { key: "maintenance", label: "Maintenance jobs", fn: (a) => a.maintenanceXlsx() },
  { key: "all", label: "Everything", fn: (a) => a.allXlsx() },
];

function GenerateReportsCard() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [intent, setIntent] = useState("building"); // "building" | "door"
  const [building, setBuilding] = useState(null); // selected building when picking a door
  const [q, setQ] = useState("");
  const buildings = useAsync(() => (open ? buildingsApi.getAll() : Promise.resolve({ data: [] })), [open]);
  const doors = useAsync(() => (building ? doorsApi.getAll(building.id) : Promise.resolve({ data: [] })), [building?.id]);

  const start = (which) => { setIntent(which); setBuilding(null); setQ(""); setOpen(true); };

  const buildingList = useMemo(() => {
    const arr = Array.isArray(buildings.data) ? buildings.data : [];
    const s = q.trim().toLowerCase();
    return s ? arr.filter((b) => `${b.name} ${b.address} ${b.postcode}`.toLowerCase().includes(s)) : arr;
  }, [buildings.data, q]);
  const doorList = useMemo(() => {
    const arr = Array.isArray(doors.data) ? doors.data : [];
    const s = q.trim().toLowerCase();
    return s ? arr.filter((d) => `${d.door_reference} ${d.location}`.toLowerCase().includes(s)) : arr;
  }, [doors.data, q]);

  const onBuilding = (b) => {
    if (intent === "building") { setOpen(false); navigate(`/reports/build?mode=building&buildingId=${b.id}`); }
    else { setBuilding(b); setQ(""); }
  };
  const onDoor = (d) => { setOpen(false); navigate(`/reports/build?mode=door&doorId=${d.id}&buildingId=${building.id}`); };

  const pickingDoors = intent === "door" && building;

  return (
    <Card className="mb-6 p-5" data-testid="generate-reports-card">
      <h3 className="mb-1 font-semibold text-slate-800">Generate a report</h3>
      <p className="mb-3 text-sm text-slate-500">Build a customised PDF — edit the wording and pick which doors to include.</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <button onClick={() => start("door")} className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 text-left transition-shadow hover:shadow-md" data-testid="open-single-door-report">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-100 text-primary"><FileText className="h-6 w-6" /></div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-slate-800">Single Door Report</p>
            <p className="text-sm text-slate-500">Detailed report for one door inspection</p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-slate-400" />
        </button>
        <button onClick={() => start("building")} className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 text-left transition-shadow hover:shadow-md" data-testid="open-building-report">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-100 text-primary"><Building2 className="h-6 w-6" /></div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-slate-800">Building Report</p>
            <p className="text-sm text-slate-500">All (or selected) doors in a building</p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-slate-400" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-hidden sm:max-w-lg" data-testid="report-picker-dialog">
          <DialogHeader>
            <DialogTitle>{pickingDoors ? "Select a door" : "Select a building"}</DialogTitle>
            <DialogDescription>{pickingDoors ? building.name : "Choose the building for this report."}</DialogDescription>
          </DialogHeader>
          <div className="relative mb-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={pickingDoors ? "Search doors…" : "Search buildings…"} className="pl-9" data-testid="report-picker-search" />
          </div>
          <div className="max-h-[55vh] space-y-1.5 overflow-auto">
            {!pickingDoors && buildings.loading && <LoadingState label="Loading buildings…" />}
            {!pickingDoors && !buildings.loading && buildingList.map((b) => (
              <button key={b.id} onClick={() => onBuilding(b)} className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2.5 text-left hover:bg-slate-50" data-testid={`report-pick-building-${b.id}`}>
                <span className="flex min-w-0 items-center gap-2"><Building2 className="h-4 w-4 shrink-0 text-primary" /><span className="truncate text-sm text-slate-700">{b.name}</span></span>
                <span className="shrink-0 text-xs text-slate-400">{b.door_count ?? 0} doors</span>
              </button>
            ))}
            {pickingDoors && doors.loading && <LoadingState label="Loading doors…" />}
            {pickingDoors && !doors.loading && doorList.length === 0 && <EmptyState title="No doors" message="This building has no doors yet." />}
            {pickingDoors && !doors.loading && doorList.map((d) => (
              <button key={d.id} onClick={() => onDoor(d)} className="flex w-full items-center gap-2 rounded-lg border border-slate-100 px-3 py-2.5 text-left hover:bg-slate-50" data-testid={`report-pick-door-${d.id}`}>
                <DoorClosed className="h-4 w-4 shrink-0 text-primary" />
                <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium text-slate-700">{d.door_reference}</span>{d.location ? <span className="block truncate text-xs text-slate-400">{d.location}</span> : null}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ExportsCard() {
  const [busy, setBusy] = useState("");
  const run = async (e) => {
    setBusy(e.key);
    try {
      const res = await e.fn(exportsApi);
      downloadBinaryResponse(res, `fdcheck-${e.key}.xlsx`);
      toast.success(`${e.label} exported`);
    } catch (err) {
      // 402 plan-limit errors already surface the global Upgrade dialog via the
      // axios interceptor, so skip the redundant error toast for those.
      if (!err?.planLimit) toast.error(apiError(err, "Could not export"));
    } finally {
      setBusy("");
    }
  };
  return (
    <Card className="mb-6 p-5" data-testid="exports-card">
      <h3 className="mb-1 flex items-center gap-2 font-semibold text-slate-800"><Sheet className="h-5 w-5 text-primary" /> Excel exports</h3>
      <p className="mb-3 text-sm text-slate-500">Download company data as spreadsheets.</p>
      <div className="flex flex-wrap gap-2">
        {EXPORTS.map((e) => (
          <Button key={e.key} variant="outline" size="sm" disabled={!!busy} onClick={() => run(e)} data-testid={`export-${e.key}`}>
            {busy === e.key ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} {e.label}
          </Button>
        ))}
      </div>
    </Card>
  );
}

export default function Reports() {
  const { data, loading, error, reload } = useAsync(() => savedReportsApi.list(), []);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { viewOnly } = usePlan();
  const isAdmin = user?.role === "admin" || user?.is_company_admin;
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [verifyFor, setVerifyFor] = useState(null);
  const [signature, setSignature] = useState("");

  const changeStatus = async (r, status, sig) => {
    setBusy(`s-${r.id}`);
    try {
      await savedReportsApi.setStatus(r.id, { status, ...(sig ? { signature: sig } : {}) });
      toast.success(status === "shared" ? "Shared to the client portal" : status === "verified" ? "Report verified" : "Moved back to draft");
      setVerifyFor(null);
      setSignature("");
      reload();
    } catch (e) {
      toast.error(apiError(e, "Could not update report status"));
    } finally {
      setBusy(null);
    }
  };

  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter(
      (r) =>
        (r.title || "").toLowerCase().includes(s) ||
        (r.building_name || "").toLowerCase().includes(s) ||
        (r.report_type || "").toLowerCase().includes(s)
    );
  }, [list, q]);

  const fetchPdf = async (r) => {
    const res = await savedReportsApi.download(r.id);
    const { pdf_base64, file_name } = res.data || {};
    if (!pdf_base64) throw new Error("This report has no PDF stored.");
    return { url: base64ToBlobUrl(pdf_base64), file_name: file_name || "report.pdf" };
  };

  const view = async (r) => {
    setBusy(`v-${r.id}`);
    try {
      const { url } = await fetchPdf(r);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error(apiError(e, "Could not open report"));
    } finally {
      setBusy(null);
    }
  };

  const download = async (r) => {
    setBusy(`d-${r.id}`);
    try {
      const { url, file_name } = await fetchPdf(r);
      const a = document.createElement("a");
      a.href = url;
      a.download = file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error(apiError(e, "Could not download report"));
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async () => {
    const r = confirmDelete;
    if (!r) return;
    setBusy(`x-${r.id}`);
    try {
      await savedReportsApi.remove(r.id);
      toast.success("Report deleted");
      setConfirmDelete(null);
      reload();
    } catch (e) {
      toast.error(apiError(e, "Could not delete report"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <PageHeader title="Reports" subtitle="Generate, export & manage inspection PDFs" testId="reports-header" />

      {viewOnly && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white px-4 py-3" data-testid="reports-view-only-banner">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600"><Eye className="h-4 w-4" /></span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800">Reports are view-only on your free trial</p>
              <p className="text-xs text-slate-500">You can preview reports on screen. Subscribe to download, print and share PDF reports.</p>
            </div>
          </div>
          <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100" onClick={() => navigate("/settings?tab=subscription")} data-testid="reports-view-only-cta">View plans</Button>
        </div>
      )}

      <GenerateReportsCard />

      <ExportsCard />

      {loading && <LoadingState label="Loading reports…" />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && (
        <>
          <div className="relative mb-4 max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search reports…" className="pl-10" value={q} onChange={(e) => setQ(e.target.value)} data-testid="reports-search" />
          </div>

          {filtered.length === 0 ? (
            <EmptyState title={list.length === 0 ? "No saved reports yet" : "No matches"} message={list.length === 0 ? "Generated report PDFs will be saved and listed here." : "Try a different search."} testId="reports-empty" />
          ) : (
            <div className="space-y-2" data-testid="reports-list">
              {filtered.map((r) => (
                <Card key={r.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`report-row-${r.id}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <FileBarChart className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-800">{r.title || r.file_name}</p>
                      <p className="truncate text-sm text-slate-500">
                        {r.building_name ? `${r.building_name} · ` : ""}{fmtDateTime(r.generated_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.verification_status && (
                      <Badge variant="outline" className={statusTone(r.verification_status)}>{prettyStatus(r.verification_status)}</Badge>
                    )}
                    {isAdmin && (r.verification_status === "draft" || !r.verification_status) && (
                      <Button size="sm" variant="outline" onClick={() => { setVerifyFor(r); setSignature(""); }} disabled={!!busy} data-testid={`report-verify-${r.id}`}>
                        <ShieldCheck className="h-4 w-4" /><span className="ml-1.5 hidden sm:inline">Verify</span>
                      </Button>
                    )}
                    {isAdmin && r.verification_status === "verified" && (
                      <>
                        <Button size="sm" onClick={() => changeStatus(r, "shared")} disabled={!!busy} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`report-share-${r.id}`}>
                          {busy === `s-${r.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}<span className="ml-1.5 hidden sm:inline">Share</span>
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => changeStatus(r, "draft")} disabled={!!busy} title="Back to draft" data-testid={`report-unverify-${r.id}`}>
                          <Undo2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    {isAdmin && r.verification_status === "shared" && (
                      <Button size="sm" variant="outline" onClick={() => changeStatus(r, "verified")} disabled={!!busy} data-testid={`report-unshare-${r.id}`}>
                        {busy === `s-${r.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />}<span className="ml-1.5 hidden sm:inline">Unshare</span>
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => view(r)} disabled={!!busy} data-testid={`report-view-${r.id}`}>
                      {busy === `v-${r.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                      <span className="ml-1.5 hidden sm:inline">View</span>
                    </Button>
                    <Button size="sm" onClick={() => download(r)} disabled={!!busy} data-testid={`report-download-${r.id}`}>
                      {busy === `d-${r.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      <span className="ml-1.5 hidden sm:inline">Download</span>
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setConfirmDelete(r)} disabled={!!busy} className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`report-delete-${r.id}`}>
                      {busy === `x-${r.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      <span className="ml-1.5 hidden sm:inline">Delete</span>
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="report-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this report?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete ? `"${confirmDelete.title || confirmDelete.file_name}" will be permanently removed. This can't be undone. You can always regenerate it from the inspection data.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="report-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doDelete(); }} className="bg-red-600 text-white hover:bg-red-700" data-testid="report-delete-confirm">
              {busy && String(busy).startsWith("x-") ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />} Delete report
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!verifyFor} onOpenChange={(o) => { if (!o) { setVerifyFor(null); setSignature(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="report-verify-dialog">
          <DialogHeader>
            <DialogTitle>Verify report</DialogTitle>
            <DialogDescription>
              Type your full name to sign off this report. This is recorded on the audit trail. Once verified you can share it to the client portal.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder="Your full name"
            data-testid="report-verify-signature"
            onKeyDown={(e) => { if (e.key === "Enter" && signature.trim()) changeStatus(verifyFor, "verified", signature.trim()); }}
          />
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setVerifyFor(null); setSignature(""); }} data-testid="report-verify-cancel">Cancel</Button>
            <Button disabled={!signature.trim() || !!busy} onClick={() => changeStatus(verifyFor, "verified", signature.trim())} data-testid="report-verify-confirm">
              {busy && String(busy).startsWith("s-") ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1.5 h-4 w-4" />} Verify &amp; sign off
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
